// autocomplete-editor.component.ts
import { AfterViewInit, Component, OnDestroy, ViewChild, ViewContainerRef } from '@angular/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { FormControl } from '@angular/forms';
import {
  MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

const AG_GRID_POPUP_CLASS = 'ag-custom-component-popup';

/**
 * Component for implementing a mat-autocomplete inside of an ag-grid.
 * Should be used as a cellEditor.
 *
 * The AG_GRID_POPUP_CLASS const is necessary for a popup/overlay (such
 * as the mat-autocomplete options) to work with ag-grid.
 * Also as of writing on 2024-05-14 it is not possible to use the config option
 * `stopEditingWhenCellsLoseFocus: true` when creating grid-options with this
 * component.
 *
 * This component also expects two params to be passed in via cellEditorParams.
 * 1. options: The list of data used to populate the mat-autocomplete
 * 2. accessProperty: The key of the field of the object type that makes up the options that will be displayed in each mat-option
 */
@Component({
  selector: 'acs-autocomplete-cell',
  template: `
    <input
      style="width: 100%;"
      type="text"
      matInput
      #cellInput
      [formControl]="cellControl"
      [matAutocomplete]="cellAutocomplete"
      #autoCompleteTrigger="matAutocompleteTrigger"
      [acsVVPHeightCalc]="viewport"
    />
    <mat-autocomplete
      autoActiveFirstOption
      #cellAutocomplete="matAutocomplete"
      (optionSelected)="onOptionSelected($event)"
    >
      <cdk-virtual-scroll-viewport #viewport itemSize="45">
        <mat-option
          *cdkVirtualFor="let option of filteredOptions | async"
          [value]="option"
          (mousedown)="onOptionClicked(option)"
        >
          {{ option[displayField] }}
        </mat-option>
      </cdk-virtual-scroll-viewport>
    </mat-autocomplete>
  `,
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: AG_GRID_POPUP_CLASS }
    }
  ]
})
export class AgGridAutocompleteEditorComponent
  implements ICellEditorAngularComp, AfterViewInit, OnDestroy {
  @ViewChild('autoCompleteTrigger', { static: false })
  autoCompleteTrigger: MatAutocompleteTrigger;
  @ViewChild('cellInput', { read: ViewContainerRef }) public cellInput: ViewContainerRef;

  private params: any;
  public cellControl = new FormControl();
  public filteredOptions: Observable<(string | number)[]>;
  public options: any[] = []; // This will be set from the params.
  public displayField: string;

  ngOnDestroy(): void {
    console.log('AgGridAutocompleteEditorComponent destroyed!');
  }

  agInit(params: ICellEditorParams): void {
    this.params = params;
    this.options = this.params.options || [];
    this.displayField = this.params.accessProperty;

    if (this.params.value) {
      this.cellControl.setValue(this.params.value);
    }

    this.filteredOptions = this.cellControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        if (typeof value === 'string') {
          return this._filter(value);
        }

        return this.options;
      })
    );
  }

  getValue(): any {
    return this.cellControl.value;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.cellInput.element.nativeElement.focus();
      this.autoCompleteTrigger.closePanel();
    });
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.cellControl.setValue(event.option.value, { emitEvent: false });
    this.params.stopEditing();
  }

  public onOptionClicked(option: any): void {
    this.cellControl.setValue(option, { emitEvent: false });
    this.params.stopEditing();
  }

  private _filter(value: string | number): any[] {
    if (this.params.data.id) {
      return;
    }

    if (!value || value.toString().trim() === '') {
      return this.options;
    }

    const filterValue = value.toString().toLowerCase();
    return this.options.filter((option) =>
      option[this.displayField].toString().toLowerCase().includes(filterValue)
    );
  }
}
